import { Component, computed, inject, Input, OnDestroy, OnInit, Signal, TemplateRef } from '@angular/core';
import {
  NbActionsModule,
  NbButtonModule,
  NbComponentType,
  NbContextMenuModule,
  NbIconModule,
  NbMediaBreakpointsService,
  NbMenuService,
  NbSearchModule,
  NbSelectModule,
  NbSidebarService,
  NbThemeModule,
  NbThemeService,
  NbTooltipModule,
  NbUserModule,
  NbWindowConfig,
  NbWindowService,
} from '@nebular/theme';

import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { NbSecurityModule } from '@nebular/security';
import {
  AnalyticsService,
  CurrentThemeService,
  KPHeaderData,
  KPMenuItemSections,
  LayoutService,
  NbUserData,
  RippleService,
  User,
} from '@res/ngg-core';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { HeaderAction } from '../../layouts';

@Component({
  standalone: true,
  selector: 'nggt-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  imports: [
    CommonModule,
    NbIconModule,
    NbSelectModule,
    NbActionsModule,
    NbSecurityModule,
    NbSearchModule,
    NbUserModule,
    NbButtonModule,
    NbContextMenuModule,
    MatRippleModule,
    NbThemeModule,
    NbTooltipModule,
    OverlayModule,
    MatMenuModule,
    MatDividerModule,
    RouterModule,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input({ required: false }) templateRefs: HeaderAction[] = [];
  @Input({ required: true }) headerData!: KPHeaderData;
  private destroy$: Subject<void> = new Subject<void>();
  private windowService = inject(NbWindowService);
  public isMaterialTheme: Signal<boolean> = computed(() => {
    return this.currentThemeService.currentThemeSignal().startsWith('material');
  });

  userPictureOnly = false;
  isOpen = false;
  user?: User;

  public themes: Array<{ value: string; name: string }> = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    {
      value: 'cosmic',
      name: 'Cosmic',
    },
    {
      value: 'corporate',
      name: 'Corporate',
    },
    {
      value: 'material-light',
      name: 'Material Light',
    },
    {
      value: 'material-dark',
      name: 'Material Dark',
    },
  ];

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private userService: NbUserData,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private rippleService: RippleService,
    private analytics: AnalyticsService,
    public currentThemeService: CurrentThemeService,
  ) {}

  public ngOnInit() {
    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: { [key: string]: User }) => (this.user = users['nick']));

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService
      .onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe((isLessThanXl: boolean) => (this.userPictureOnly = isLessThanXl));

    this.themeService
      .onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      )
      .subscribe((themeName) => {
        this.rippleService.toggle(themeName?.startsWith('material'));
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get getMenuitems() {
    return this.headerData?.userMenu?.filter((x) => x.section === KPMenuItemSections.MidSection);
  }

  changeTheme(themeName: string) {
    this.currentThemeService.setCurrentTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  toggleNotificationbar(): boolean {
    this.sidebarService.toggle(false, 'notification-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  startSearch() {
    this.analytics.trackEvent('start_search');
  }

  trackEmailClick() {
    this.analytics.trackEvent('click_contact_email', { action: 'click' });
  }

  // Returns `false` only where it did before: an Angular event binding turns that into
  // `preventDefault()` on the originating click.
  onAction(headerAction: HeaderAction): boolean | undefined {
    switch (`${headerAction.key}-${headerAction.actionType}`) {
      case 'add-window':
        console.log('add-window', this.templateRefs);
        if (headerAction?.action) {
          this.openWindowForm(headerAction.action);
        }
        return undefined;
      case 'toggle-notification':
        return this.toggleNotificationbar();

      default:
        return undefined;
    }
  }

  public openWindowForm(action: { ref: TemplateRef<unknown> | NbComponentType; config: Partial<NbWindowConfig> }) {
    // this.windowService.open(WindowFormComponent, { title: `Window-${actionType}` });
    this.windowService.open(action.ref, action.config);
  }
}
